
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Trash2 } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { Asset, EntityQuery, Query } from "@the-real-insight/tri-model";
import { useAdministrationAPI } from "../AdministrationAPI";
import { useModalContext } from "../components/ModalProvider";
import { IconSelector } from "../components/IconSelector";
import Section from "../components/Section";
import { userAPI } from "../UserAPI";
import { assetsOnFloorFilter } from "../utilities/activeAssets";

import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";

interface Properties {
  space: any;
}

/**
 * Equipment table for a space: same columns as the Floor equipment list, filtered to
 * assets whose `space` reference matches this space (loaded via floor query + filter).
 */
export default function SpaceEquipmentPanel(properties: Properties) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pushError, showDeleteDialog } = useModalContext() as any;
  const { updateAsset, deleteAsset } = useAdministrationAPI();
  const [metadata, setMetadata] = useState<Record<string, any>>({});
  const [assets, setAssets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const space = properties.space;

  const refreshAssets = useCallback(async () => {
    if (!space?._id) {
      setAssets([]);
      setLoading(false);
      return;
    }
    const floorId =
      typeof space.floor === "string" ? space.floor : space.floor?._id;
    if (!floorId) {
      setAssets([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await userAPI.query(
        new Query(
          new EntityQuery(
            Asset.type,
            [],
            undefined,
            assetsOnFloorFilter(floorId),
          ),
        ),
      );
      const list = (result || []) as any[];
      const filtered = list.filter((a) => {
        const s = a.space;
        const sid =
          s == null ? null : typeof s === "string" ? s : s?._id;
        return sid === space._id;
      });
      setAssets(filtered);
    } catch (e) {
      pushError(e);
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, [space?._id, space?.floor, pushError]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const meta = await userAPI.getDIN276Metadata();
        if (!cancelled) {
          setMetadata(
            meta.reduce((map: Record<string, any>, category: any) => {
              map[category.code] = category;
              return map;
            }, {}),
          );
        }
      } catch {
        if (!cancelled) setMetadata({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    void refreshAssets();
  }, [refreshAssets]);

  const assetsColumns = useMemo<ColumnDef<any, any>[]>(() => [
    {
      id: "delete",
      header: "",
      size: 50,
      cell: ({ row }) => (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t("ui.deleteDeviceEquipment")}
          onClick={() =>
            showDeleteDialog(
              t("ui.deleteDeviceEquipment"),
              row.original.id,
              async () => {
                await deleteAsset(row.original);
                await refreshAssets();
              },
            )
          }
        >
          <Trash2 aria-hidden="true" className="h-4 w-4" />
        </Button>
      ),
    },
    {
      id: "name",
      header: "Name/ID",
      size: 200,
      cell: ({ row }) => (
        <div className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 break-words font-bold">{row.original.name || row.original.id}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0"
            aria-label={t("infoList.openLinked")}
            onClick={() =>
              navigate(
                `/entity?selectedEntityType=Asset&selectedEntity=${row.original._id}`,
              )
            }
          >
            <ExternalLink aria-hidden="true" className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
    {
      id: "schemaCategory",
      header: "DIN 276 Type",
      size: 200,
      accessorFn: (asset: any) => asset.schemaCategory,
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="text-xs">{row.original.schemaCategory}</div>
          <div className="text-xs">
            {metadata[row.original.schemaCategory]
              ? metadata[row.original.schemaCategory].name
              : ""}
          </div>
        </div>
      ),
    },
    {
      id: "icon",
      header: () => <div className="text-right">Symbol</div>,
      size: 80,
      cell: ({ row }) => (
        <div className="flex justify-end">
          <IconSelector
            icon={row.original.icon}
            iconChanged={async (icon: string) => {
              const asset = row.original;
              const newAsset = { ...asset, icon };
              const newAssets = [...assets];
              newAssets[assets.indexOf(asset)] = newAsset;
              setAssets(newAssets);
              await updateAsset(newAsset);
            }}
          ></IconSelector>
        </div>
      ),
    },
  ], [assets, metadata, navigate, refreshAssets, showDeleteDialog, deleteAsset, updateAsset, t]);

  return (
    <div className="min-w-0">
      <Section title={t("ui.devicesAndEquipment")}>
        <div className="overflow-x-auto">
          <DataTable
            columns={assetsColumns}
            data={assets}
            pageSize={10}
            loading={loading}
            emptyMessage={t("settingsPanel.dataTable.noRecordsText")}
          />
        </div>
      </Section>
    </div>
  );
}
